import React from 'react';
import {
  ChevronDown, ChevronRight, CheckCircle, MessageSquare, Shield,
  FileText, AlertCircle, Check, Search
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import Pagination from '../ui/Pagination';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CaisseStatutBadge } from './CentralBadges';
import { formatMontant } from './nosDepensesUtils';

/**
 * Le retard d'envoi, en jours pleins.
 *
 * Une remise faite le lendemain matin est normale — la journee se cloture
 * apres coup. Au-dela, c'est un ecart qui merite d'etre vu.
 */
const retardEnvoi = (dateJournee, dateEnvoi) => {
  if (!dateJournee || !dateEnvoi) return null;
  const jour = new Date(dateJournee);
  const envoi = new Date(dateEnvoi);
  if (Number.isNaN(jour.getTime()) || Number.isNaN(envoi.getTime())) return null;
  const aJour = new Date(jour.getFullYear(), jour.getMonth(), jour.getDate());
  const aEnvoi = new Date(envoi.getFullYear(), envoi.getMonth(), envoi.getDate());
  return Math.round((aEnvoi - aJour) / 86400000);
};

const heureCourte = (valeur) => {
  if (!valeur) return null;
  const d = new Date(valeur);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const jourCourt = (valeur) => {
  if (!valeur) return null;
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('fr-FR');
};

/**
 * Le gabarit des colonnes, declare UNE FOIS.
 *
 * L'en-tete et les lignes s'y referent tous les deux : c'est la seule facon de
 * garantir qu'une colonne tombe au meme endroit partout. Chaque ligne portait
 * auparavant sa propre grille, dimensionnee sur son contenu.
 */
const GRILLE_CAISSE = 'grid grid-cols-[minmax(200px,1.3fr)_100px_150px_170px_repeat(5,110px)] gap-x-4 items-center';

const STATUT_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'en_attente', label: 'En attente' },
  { value: 'avec_observation', label: 'Avec observations' },
  { value: 'validee', label: 'Validées' }
];

// Tableau des transactions d'une caisse -- en-tete colore facon MaCaisse.
const TransactionsTable = ({
  caisse, selectedTransactions, toggleTransaction, selectAllTransactions,
  onValiderTransaction, onSignalerTransaction, onValiderSelection, isLoading
}) => {
  const transactions = caisse.transactions || [];
  const selectedCount = transactions.filter((t) => selectedTransactions.has(t.id)).length;

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-[#246990]/25">
        <FileText size={40} className="mx-auto text-gray-400 mb-3" />
        <p className="text-gray-600 font-medium">Aucune transaction détaillée</p>
        <p className="text-gray-500 text-sm mt-1">Le détail des transactions de cette remise n'est pas disponible.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={transactions.length > 0 && selectedCount === transactions.length}
            onChange={() => selectAllTransactions(caisse)}
            className="rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
          />
          Tout sélectionner ({transactions.length})
        </label>
        {selectedCount > 0 && (
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={isLoading}
            onClick={() => onValiderSelection(caisse)}
          >
            <Check size={16} className="mr-2" />
            Valider la sélection ({selectedCount})
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#eef7fb] border-b border-[#246990]/20">
            <tr>
              <th className="px-4 py-3 w-10"></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#246990] uppercase">Date &amp; Heure</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#246990] uppercase">Description</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-[#246990] uppercase">Montant</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-[#246990] uppercase">Justificatif</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-[#246990] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#246990]/10">
            {transactions.map((transaction) => {
              const aJustificatif = Boolean(transaction.justificatif || transaction.justificatif_fichier_url);
              return (
                <tr key={transaction.id} className="hover:bg-[#eef7fb] transition-colors">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedTransactions.has(transaction.id)}
                      onChange={() => toggleTransaction(transaction.id)}
                      className="rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-900 font-medium">
                      {new Date(transaction.date_transaction).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(transaction.date_transaction).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{transaction.description || '-'}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className={`text-sm font-bold ${transaction.type === 'encaissement' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {transaction.type === 'encaissement' ? '+' : '-'}{formatMontant(transaction.montant)}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {aJustificatif ? (
                      <button className="p-2 text-[#246990] hover:bg-[#eef7fb] rounded-lg transition-colors" title="Voir justificatif">
                        <FileText size={18} />
                      </button>
                    ) : (
                      <div className="flex items-center justify-center">
                        <AlertCircle size={18} className="text-amber-500" title="Justificatif manquant" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        className="p-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                        title="Valider cette transaction"
                        onClick={() => onValiderTransaction(caisse.id, transaction.id)}
                      >
                        <CheckCircle size={16} />
                      </button>
                      <button
                        className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                        title={aJustificatif ? 'Ajouter une observation' : 'Demander le justificatif'}
                        onClick={() => onSignalerTransaction(caisse.id, transaction.id, aJustificatif ? 'observation' : 'justificatif')}
                      >
                        <MessageSquare size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CaissesListSection = ({
  isExpanded, onToggleSection,
  isLoading, caisses, filteredCaisses, expandedCaisses, toggleCaisse,
  selectedTransactions, toggleTransaction, selectAllTransactions,
  onValiderCaisse, onObservationGlobale, onValiderTransaction, onSignalerTransaction, onValiderSelection,
  pagination, setPagination,
  filterStatut, setFilterStatut, searchGestionnaire, setSearchGestionnaire
}) => {
  return (
    <div className="px-6">
      <Card>
        <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={onToggleSection}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronDown size={24} className="text-gray-600" />
              ) : (
                <ChevronRight size={24} className="text-gray-600" />
              )}
              <div>
                <CardTitle className="text-xl text-emerald-600 flex items-center gap-2">
                  <Shield size={24} />
                  Remises des gestionnaires
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  contrôle et validation des caisses journalières remises
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-6 space-y-6">
            {/* Filtres */}
            <div className="border-2 border-gray-200 rounded-lg bg-gray-50/50 p-4">
              <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_220px] gap-4 items-end">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Statut des caisses</label>
                  <Select value={filterStatut} onValueChange={setFilterStatut}>
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Gestionnaire</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Rechercher un gestionnaire..."
                      value={searchGestionnaire}
                      onChange={(e) => setSearchGestionnaire(e.target.value)}
                      className="pl-10 bg-white"
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-[#246990]/20 bg-[#246990]/5 px-4 py-3">
                  <p className="text-xs font-semibold uppercase text-[#246990]">Résultat</p>
                  <p className="text-lg font-bold text-gray-900">{filteredCaisses.length} caisse(s)</p>
                  <p className="text-xs text-gray-600">sur {caisses.length} au total</p>
                </div>
              </div>
            </div>

            {/* Liste des caisses */}
            {isLoading ? (
              <div className="flex justify-center items-center h-40">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-emerald-600 font-medium">Chargement...</span>
                </div>
              </div>
            ) : filteredCaisses.length === 0 ? (
              <EtatVide
                icone={Shield}
                titre="Aucune caisse trouvée"
                description="Aucune remise ne correspond aux filtres retenus. Relâchez-en un pour voir davantage de résultats."
              />
            ) : (
              <div className="overflow-x-auto">
                {/* La charte demande qu'un contenu large défile chez lui
                    plutôt que d'écraser la page : neuf colonnes ne tiennent
                    pas sous 1180 px.

                    `px-[18px]` et non `px-4` : chaque carte porte un
                    `border-2`, donc son contenu commence 2 px plus loin que
                    l'en-tête. Sans cette compensation les colonnes glissent. */}
                {/* Les intitules, une fois pour toute la liste. Repetes sur chaque
                    ligne, ils pesaient autant que les valeurs qu'ils designent. */}
                <div className={`${GRILLE_CAISSE} min-w-[1180px] border-b border-gray-200 px-[18px] pb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500`}>
                  <span>Gestionnaire</span>
                  <span>Journée</span>
                  <span>Envoyée</span>
                  <span>Statut</span>
                  <span className="text-right">Encaissements</span>
                  <span className="text-right">Dépenses</span>
                  <span className="text-right">Solde attendu</span>
                  <span className="text-right">Remis</span>
                  <span className="text-right">Écart</span>
                </div>
              
                <div className="mt-3 space-y-3">
                {filteredCaisses.map((caisse) => {
                  const ecart = Number(caisse.ecart || 0);
                  const estOuverte = expandedCaisses.has(caisse.id);
                  const retard = retardEnvoi(caisse.date_cloture_caisse, caisse.date_envoi);
                  const proprietaire = [caisse.gestionnaire_prenom, caisse.gestionnaire_nom].filter(Boolean).join(' ');
                  const cloturePar = [caisse.cloture_par_prenom, caisse.cloture_par_nom].filter(Boolean).join(' ');
                  const validePar = [caisse.validee_par_prenom, caisse.validee_par_nom].filter(Boolean).join(' ');
                  // Trois états, trois couleurs de sens.
                  // L'ambre remplace l'orange sur l'observation : la charte
                  // donne l'ambre pour « l'avertissement qui n'empêche pas
                  // d'agir », ce qu'est exactement une observation, et réserve
                  // l'orange à la lecture comptable. L'attente prend la teinte
                  // de marque plutôt qu'un bleu générique.
                  const borderTone = caisse.statut === 'avec_observation'
                    ? 'border-amber-200'
                    : caisse.statut === 'validee'
                      ? 'border-emerald-200'
                      : 'border-[#246990]/20';
                  const headerTone = caisse.statut === 'avec_observation'
                    ? 'bg-amber-50 border-amber-200'
                    : caisse.statut === 'validee'
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-[#eef7fb] border-[#246990]/20';

                  return (
                    <div key={caisse.id} className={`border-2 ${borderTone} rounded-lg overflow-hidden`}>
                      <div
                        className={`p-4 ${headerTone} border-b cursor-pointer hover:brightness-95 transition-all`}
                        onClick={() => toggleCaisse(caisse.id)}
                      >
                        <div className={`${GRILLE_CAISSE} min-w-[1180px]`}>

                          {/* Gestionnaire */}
                          <div className="flex min-w-0 items-center gap-2.5">
                            {estOuverte ? (
                              <ChevronDown size={16} className="flex-shrink-0 text-gray-500" />
                            ) : (
                              <ChevronRight size={16} className="flex-shrink-0 text-gray-500" />
                            )}
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#246990]">
                              <span className="text-[11px] font-bold text-white">
                                {caisse.gestionnaire_prenom?.[0]}{caisse.gestionnaire_nom?.[0]}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {proprietaire}
                              </p>
                              <p className="truncate text-[11px] text-gray-500">
                                {caisse.gestionnaire_poste}
                              </p>
                            </div>
                          </div>

                          {/* Journée couverte — et non la date d'envoi : le
                              libellé « Clôturée le » confondait les deux. */}
                          <p className="text-xs font-medium tabular-nums text-gray-800">
                            {jourCourt(caisse.date_cloture_caisse) || '—'}
                          </p>

                          {/* Envoi, avec le retard s'il dépasse le lendemain.
                              Une remise faite le matin suivant est normale :
                              la journée se clôture après coup. */}
                          <div className="min-w-0">
                            {caisse.date_envoi ? (
                              <>
                                <p className="flex items-center gap-1.5 text-xs font-medium tabular-nums text-gray-800">
                                  {jourCourt(caisse.date_envoi)}
                                  {retard !== null && retard > 1 && (
                                    <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                      +{retard} j
                                    </span>
                                  )}
                                </p>
                                <p className="text-[11px] tabular-nums text-gray-500">
                                  {heureCourte(caisse.date_envoi) || ''}
                                  {cloturePar && cloturePar !== proprietaire ? ` · par ${cloturePar}` : ''}
                                </p>
                              </>
                            ) : (
                              <p className="text-xs text-gray-400">—</p>
                            )}
                          </div>

                          {/* Statut, et la validation quand elle a eu lieu. */}
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <CaisseStatutBadge statut={caisse.statut} />
                              {caisse.nb_observations > 0 && (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                  {caisse.nb_observations} obs.
                                </span>
                              )}
                            </div>
                            {caisse.statut === 'validee' && (
                              <p className="mt-0.5 truncate text-[11px] text-emerald-700">
                                {validePar ? `${validePar} · ` : ''}
                                {jourCourt(caisse.date_validation) || ''}
                              </p>
                            )}
                          </div>

                          {/* Les cinq montants. Chiffres tabulaires et
                              alignement à droite : les unités sous les unités. */}
                          {[
                            { valeur: caisse.total_encaissements, ton: 'text-emerald-600' },
                            { valeur: caisse.total_depenses, ton: 'text-red-600' },
                            { valeur: caisse.solde, ton: 'text-[#3084b5]' },
                            { valeur: caisse.montant_declare ?? caisse.solde, ton: 'text-[#246990]' },
                            { valeur: ecart, ton: ecart === 0 ? 'text-emerald-600' : 'text-amber-600' }
                          ].map((colonne, index) => (
                            <p key={index} className={`text-right text-sm font-bold tabular-nums ${colonne.ton}`}>
                              {formatMontant(colonne.valeur)}
                            </p>
                          ))}
                        </div>

                        {caisse.note_gestionnaire && (
                          <div className="mt-4 bg-white border border-[#246990]/20 rounded-lg p-3">
                            <p className="text-sm text-[#246990] flex items-start gap-2">
                              <MessageSquare size={16} className="mt-0.5 flex-shrink-0" />
                              <span><strong>Note de clôture :</strong> {caisse.note_gestionnaire}</span>
                            </p>
                          </div>
                        )}

                        {caisse.motif_ecart && (
                          <div className="mt-3 bg-white border border-amber-200 rounded-lg p-3">
                            <p className="text-sm text-amber-900 flex items-start gap-2">
                              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                              <span><strong>Motif de l'écart :</strong> {caisse.motif_ecart}</span>
                            </p>
                          </div>
                        )}
                      </div>

                      {estOuverte && caisse.statut !== 'validee' && (
                        <div className="p-4 space-y-4">
                          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                            <p className="text-sm text-gray-600">Actions de contrôle pour cette caisse</p>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => onValiderCaisse(caisse.id, `${caisse.gestionnaire_prenom} ${caisse.gestionnaire_nom}`)}
                              >
                                <CheckCircle size={16} className="mr-2" />
                                Valider tout
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-600 border-amber-600 hover:bg-amber-50"
                                onClick={() => onObservationGlobale(caisse.id)}
                              >
                                <MessageSquare size={16} className="mr-2" />
                                Observation globale
                              </Button>
                            </div>
                          </div>

                          <TransactionsTable
                            caisse={caisse}
                            selectedTransactions={selectedTransactions}
                            toggleTransaction={toggleTransaction}
                            selectAllTransactions={selectAllTransactions}
                            onValiderTransaction={onValiderTransaction}
                            onSignalerTransaction={onSignalerTransaction}
                            onValiderSelection={onValiderSelection}
                            isLoading={isLoading}
                          />
                        </div>
                      )}

                      {estOuverte && caisse.statut === 'validee' && (
                        <div className="p-6 text-center">
                          <CheckCircle size={36} className="mx-auto text-emerald-500 mb-2" />
                          <p className="text-gray-700 font-medium">Caisse déjà validée</p>
                          <p className="text-gray-500 text-sm mt-1">
                            Les transactions de cette remise sont définitivement intégrées au solde central.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            )}

            {/* Pagination */}
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                nombrePages={pagination.total_pages}
                total={pagination.total}
                parPage={pagination.limit}
                onChangerPage={(page) => setPagination((prev) => ({ ...prev, page }))}
                libelleElement="caisse"
              />
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default CaissesListSection;
